'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  results: string[];
  metrics: {
    label: string;
    value: string;
  }[];
  testimonial: string;
  author: string;
  role: string;
}

const CaseStudiesSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('All');
  const [filteredStudies, setFilteredStudies] = useState<CaseStudy[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      company: 'Global Financial Services',
      industry: 'Financial Services',
      challenge: 'A multinational bank struggled with AI adoption across 15,000 employees. Initial deployment faced 60% resistance, with teams reverting to manual processes within weeks.',
      solution: 'Implemented our five-phase methodology with focus on leadership coaching, role-based training, and change champion networks across all business units.',
      results: [
        'Achieved 85% user adoption within 6 months',
        'Reduced processing time by 40%',
        'Increased employee satisfaction scores by 32%',
        'Delivered £2.8M annual cost savings'
      ],
      metrics: [
        { label: 'Adoption Rate', value: '85%' },
        { label: 'Time Savings', value: '40%' },
        { label: 'ROI', value: '£2.8M' }
      ],
      testimonial: 'AI Change Leaders transformed our approach from technology-first to people-first. The results exceeded our expectations.',
      author: 'Sarah Mitchell',
      role: 'Chief Digital Officer'
    },
    {
      id: 2,
      company: 'Healthcare Provider Network',
      industry: 'Healthcare',
      challenge: 'Regional healthcare network needed to deploy AI diagnostic tools across 50 facilities while maintaining clinical quality and addressing physician concerns about AI reliability.',
      solution: 'Developed comprehensive change strategy with clinical leadership engagement, evidence-based training, and phased rollout with continuous feedback loops.',
      results: [
        'Achieved 92% physician adoption',
        'Improved diagnostic accuracy by 28%',
        'Reduced patient wait times by 35%',
        'Zero clinical incidents during rollout'
      ],
      metrics: [
        { label: 'Physician Adoption', value: '92%' },
        { label: 'Accuracy Gain', value: '28%' },
        { label: 'Wait Time Reduction', value: '35%' }
      ],
      testimonial: 'The human-centered approach ensured our clinical teams felt empowered rather than threatened by AI technology.',
      author: 'Dr. James Patterson',
      role: 'Medical Director'
    },
    {
      id: 3,
      company: 'Manufacturing Corporation',
      industry: 'Manufacturing',
      challenge: 'Global manufacturer faced production inefficiencies and quality control issues. Previous AI implementation failed due to workforce resistance and inadequate training.',
      solution: 'Redesigned implementation with focus on shop floor engagement, hands-on training, and demonstrable quick wins to build confidence and momentum.',
      results: [
        'Increased production efficiency by 45%',
        'Reduced defect rates by 52%',
        'Achieved 88% workforce adoption',
        'Delivered £3.2M annual savings'
      ],
      metrics: [
        { label: 'Efficiency Gain', value: '45%' },
        { label: 'Defect Reduction', value: '52%' },
        { label: 'Annual Savings', value: '£3.2M' }
      ],
      testimonial: 'The practical, hands-on approach resonated with our workforce. We saw immediate improvements in both adoption and results.',
      author: 'Michael Chen',
      role: 'VP Operations'
    }
  ];

  const industries = ['All', 'Financial Services', 'Healthcare', 'Manufacturing'];

  useEffect(() => {
    if (!isHydrated) return;
    
    if (selectedIndustry === 'All') {
      setFilteredStudies(caseStudies);
    } else {
      setFilteredStudies(caseStudies.filter(study => study.industry === selectedIndustry));
    }
  }, [selectedIndustry, isHydrated]);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/2 mx-auto"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-muted rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 rounded-full mb-4">
            <Icon name="DocumentCheckIcon" size={20} variant="solid" className="text-success" />
            <span className="text-sm font-semibold font-body text-success">Client Success Stories</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4">
            Real Results from Real Organizations
          </h2>
          
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            See how we've helped organizations across industries achieve successful AI transformation through human-centered change leadership.
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {industries.map((industry) => (
            <button
              key={industry}
              onClick={() => setSelectedIndustry(industry)}
              className={`px-6 py-2 rounded-full font-medium font-body text-sm transition-all duration-300 ${
                selectedIndustry === industry
                  ? 'bg-primary text-primary-foreground shadow-subtle'
                  : 'bg-card text-foreground border border-border hover:border-primary/30'
              }`}
            >
              {industry}
            </button>
          ))}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {filteredStudies.map((study) => (
            <div
              key={study.id}
              className="bg-white rounded-xl shadow-card hover:shadow-strong transition-all duration-300 border border-border overflow-hidden flex flex-col"
            >
              <div className="p-6 flex-1">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold font-body rounded-full">
                    {study.industry}
                  </span>
                  <Icon name="BuildingOfficeIcon" size={20} variant="outline" className="text-muted-foreground" />
                </div>
                
                <h3 className="text-xl font-bold font-headline text-foreground mb-3">
                  {study.company}
                </h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground font-headline mb-2 flex items-center gap-2">
                      <Icon name="ExclamationCircleIcon" size={16} variant="outline" className="text-error" />
                      Challenge
                    </h4>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {study.challenge}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-foreground font-headline mb-2 flex items-center gap-2">
                      <Icon name="LightBulbIcon" size={16} variant="outline" className="text-accent" />
                      Solution
                    </h4>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {study.solution}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-foreground font-headline mb-2 flex items-center gap-2">
                      <Icon name="ChartBarIcon" size={16} variant="outline" className="text-success" />
                      Results
                    </h4>
                    <ul className="space-y-1.5">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Icon name="CheckCircleIcon" size={14} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground font-body">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {study.metrics.map((metric, idx) => (
                    <div key={idx} className="text-center p-3 bg-card rounded-lg border border-border">
                      <div className="text-lg font-bold text-primary font-headline">{metric.value}</div>
                      <div className="text-xs text-muted-foreground font-body">{metric.label}</div>
                    </div>
                  ))}
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg border-l-4 border-primary">
                  <p className="text-sm text-foreground font-body italic mb-2">
                    "{study.testimonial}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="UserIcon" size={16} variant="solid" className="text-primary" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-foreground font-body">{study.author}</div>
                      <div className="text-xs text-muted-foreground font-body">{study.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;